import React, { useState, useCallback, useEffect } from 'react';
import {
  CloudArrowUpIcon,
  XMarkIcon,
  PhotoIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { mediaApi } from '../../services/mediaApi';
import axios from 'axios';

interface MediaUploaderProps {
  folderId?: number | null;
  category?: string;
  onClose: () => void;
  onUploadComplete: () => void;
}

interface UploadFile {
  file: File;
  id: string;
  preview?: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  category: string;
  compressionQuality: number | 'auto'; // 5-95 in increments of 5, or 'auto'
  metadata: {
    title: string;
    caption: string;
    alt_text: string;
    tags: string;
  };
}

export default function MediaUploader({
  folderId,
  category: defaultCategory = 'general',
  onClose,
  onUploadComplete
}: MediaUploaderProps) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await axios.get('https://admin.clarityofconcept.com/api/media/categories', {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      addFiles(selectedFiles);
    }
  };

  const addFiles = (newFiles: File[]) => {
    const validFiles = newFiles.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB
      return isImage && isValidSize;
    });

    const uploadFiles: UploadFile[] = validFiles.map(file => {
      const id = Math.random().toString(36).substring(7);
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;

      // Auto-detect category based on filename
      let detectedCategory = defaultCategory;
      const filename = file.name.toLowerCase();
      if (filename.includes('map')) detectedCategory = 'map';
      else if (filename.includes('diagram')) detectedCategory = 'diagram';
      else if (filename.includes('news')) detectedCategory = 'news';
      else if (filename.includes('quiz')) detectedCategory = 'quiz';

      // Auto-detect compression level based on category
      let compressionQuality: number | 'auto' = 'auto';
      if (detectedCategory === 'map') compressionQuality = 60; // 40% compression for maps
      else if (detectedCategory === 'diagram') compressionQuality = 85; // 15% compression for diagrams

      return {
        file,
        id,
        preview,
        status: 'pending',
        progress: 0,
        category: detectedCategory,
        compressionQuality,
        metadata: {
          title: file.name.replace(/\.[^/.]+$/, ''),
          caption: '',
          alt_text: '',
          tags: detectTags(file.name)
        }
      };
    });

    setFiles(prev => [...prev, ...uploadFiles]);
  };

  const detectTags = (filename: string): string => {
    const tags: string[] = [];
    const name = filename.toLowerCase();

    // Detect category tags
    if (name.includes('map')) tags.push('map');
    if (name.includes('diagram')) tags.push('diagram');
    if (name.includes('chart')) tags.push('chart');
    if (name.includes('graph')) tags.push('graph');

    // Detect subject tags
    if (name.includes('history')) tags.push('history');
    if (name.includes('geography')) tags.push('geography');
    if (name.includes('polity')) tags.push('polity');
    if (name.includes('economy')) tags.push('economy');
    if (name.includes('science')) tags.push('science');

    return tags.join(', ');
  };

  const updateFileMetadata = (id: string, field: string, value: string) => {
    setFiles(prev =>
      prev.map(f =>
        f.id === id
          ? { ...f, metadata: { ...f.metadata, [field]: value } }
          : f
      )
    );
  };

  const updateFileCategory = (id: string, category: string) => {
    setFiles(prev =>
      prev.map(f =>
        f.id === id ? { ...f, category } : f
      )
    );
  };

  const updateFileCompression = (id: string, compressionQuality: number | 'auto') => {
    setFiles(prev =>
      prev.map(f =>
        f.id === id ? { ...f, compressionQuality } : f
      )
    );
  };

  const removeFile = (id: string) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  const uploadFiles = async () => {
    setIsUploading(true);

    for (const uploadFile of files) {
      if (uploadFile.status === 'success') continue;

      setFiles(prev =>
        prev.map(f =>
          f.id === uploadFile.id
            ? { ...f, status: 'uploading', progress: 0 }
            : f
        )
      );

      try {
        const formData = new FormData();
        formData.append('file', uploadFile.file);
        formData.append('title', uploadFile.metadata.title);
        formData.append('caption', uploadFile.metadata.caption);
        formData.append('alt_text', uploadFile.metadata.alt_text);
        formData.append('tags', uploadFile.metadata.tags);
        formData.append('category', uploadFile.category);
        formData.append('compression_quality', String(uploadFile.compressionQuality));
        if (folderId) {
          formData.append('folder_id', folderId.toString());
        }

        await mediaApi.upload(formData, {
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;

            setFiles(prev =>
              prev.map(f =>
                f.id === uploadFile.id ? { ...f, progress } : f
              )
            );
          }
        });

        setFiles(prev =>
          prev.map(f =>
            f.id === uploadFile.id
              ? { ...f, status: 'success', progress: 100 }
              : f
          )
        );
      } catch (error) {
        setFiles(prev =>
          prev.map(f =>
            f.id === uploadFile.id
              ? { ...f, status: 'error', error: 'Upload failed' }
              : f
          )
        );
      }
    }

    setIsUploading(false);
    const allSuccess = files.every(f => f.status === 'success');
    if (allSuccess) {
      setTimeout(() => {
        onUploadComplete();
      }, 1000);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Upload Media</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {files.length === 0 ? (
            /* Drop Zone */
            <div
              className={`
                border-2 border-dashed rounded-lg p-12 text-center transition-colors
                ${isDragging
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                }
              `}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <CloudArrowUpIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">
                Drop files here to upload
              </p>
              <p className="text-sm text-gray-500 mb-4">
                or click to select files
              </p>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer"
              >
                Select Files
              </label>
              <p className="text-xs text-gray-500 mt-4">
                Supports: JPG, PNG, WebP, GIF, TIFF (Max 50MB)
              </p>
            </div>
          ) : (
            /* File List */
            <div className="space-y-4">
              {files.map((uploadFile) => (
                <div
                  key={uploadFile.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                >
                  <div className="flex gap-4">
                    {/* Preview */}
                    <div className="flex-shrink-0">
                      {uploadFile.preview ? (
                        <img
                          src={uploadFile.preview}
                          alt={uploadFile.file.name}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                          <DocumentTextIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{uploadFile.file.name}</p>
                          <p className="text-sm text-gray-500">
                            {formatFileSize(uploadFile.file.size)}
                          </p>
                        </div>
                        <button
                          onClick={() => removeFile(uploadFile.id)}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          disabled={uploadFile.status === 'uploading'}
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Metadata Fields */}
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={uploadFile.category}
                          onChange={(e) => updateFileCategory(uploadFile.id, e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                          disabled={uploadFile.status !== 'pending'}
                        >
                          {categories.map(cat => (
                            <option key={cat.slug} value={cat.slug}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                        <select
                          value={uploadFile.compressionQuality}
                          onChange={(e) => updateFileCompression(uploadFile.id, e.target.value === 'auto' ? 'auto' : parseInt(e.target.value))}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                          disabled={uploadFile.status !== 'pending'}
                          title="Compression Quality"
                        >
                          <option value="auto">Auto</option>
                          <option value="95">5% compression (95% quality)</option>
                          <option value="90">10% compression (90% quality)</option>
                          <option value="85">15% compression (85% quality)</option>
                          <option value="80">20% compression (80% quality)</option>
                          <option value="75">25% compression (75% quality)</option>
                          <option value="70">30% compression (70% quality)</option>
                          <option value="65">35% compression (65% quality)</option>
                          <option value="60">40% compression (60% quality)</option>
                          <option value="55">45% compression (55% quality)</option>
                          <option value="50">50% compression (50% quality)</option>
                          <option value="45">55% compression (45% quality)</option>
                          <option value="40">60% compression (40% quality)</option>
                          <option value="35">65% compression (35% quality)</option>
                          <option value="30">70% compression (30% quality)</option>
                          <option value="25">75% compression (25% quality)</option>
                          <option value="20">80% compression (20% quality)</option>
                          <option value="15">85% compression (15% quality)</option>
                          <option value="10">90% compression (10% quality)</option>
                          <option value="5">95% compression (5% quality)</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Title"
                          value={uploadFile.metadata.title}
                          onChange={(e) => updateFileMetadata(uploadFile.id, 'title', e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 col-span-2"
                          disabled={uploadFile.status !== 'pending'}
                        />
                        <input
                          type="text"
                          placeholder="Caption"
                          value={uploadFile.metadata.caption}
                          onChange={(e) => updateFileMetadata(uploadFile.id, 'caption', e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 col-span-2"
                          disabled={uploadFile.status !== 'pending'}
                        />
                        <input
                          type="text"
                          placeholder="Tags (comma separated)"
                          value={uploadFile.metadata.tags}
                          onChange={(e) => updateFileMetadata(uploadFile.id, 'tags', e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 col-span-2"
                          disabled={uploadFile.status !== 'pending'}
                        />
                      </div>

                      {/* Progress Bar */}
                      {uploadFile.status === 'uploading' && (
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${uploadFile.progress}%` }}
                          />
                        </div>
                      )}

                      {/* Status */}
                      {uploadFile.status === 'success' && (
                        <div className="flex items-center text-green-600 text-sm">
                          <CheckCircleIcon className="w-4 h-4 mr-1" />
                          Upload complete
                        </div>
                      )}
                      {uploadFile.status === 'error' && (
                        <div className="flex items-center text-red-600 text-sm">
                          <ExclamationCircleIcon className="w-4 h-4 mr-1" />
                          {uploadFile.error}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Add More Files */}
              <div className="text-center">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload-more"
                />
                <label
                  htmlFor="file-upload-more"
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 cursor-pointer"
                >
                  + Add more files
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
          <div className="text-sm text-gray-500">
            {files.length} file{files.length !== 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={uploadFiles}
              disabled={files.length === 0 || isUploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Uploading...' : 'Upload All'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}